import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { AuditActionType, AuditResourceType } from "../types";
import { FilterOptions } from "../useAuditController";
import { RiSearch2Line, RiFilterLine, RiCloseLine } from "react-icons/ri";
import { DatePicker } from "./DatePicker";

interface AuditFiltersProps {
  filters: FilterOptions;
  updateFilters: (newFilters: Partial<FilterOptions>) => void;
  resetFilters: () => void;
  uniqueUsers: () => { id: string; name: string; email: string }[];
}

export const AuditFilters: React.FC<AuditFiltersProps> = ({
  filters,
  updateFilters,
  resetFilters,
  uniqueUsers,
}) => {
  const actionTypes: AuditActionType[] = [
    "login",
    "logout",
    "create",
    "update",
    "delete",
    "view",
    "download",
    "generate",
    "upload",
  ];

  const resourceTypes: AuditResourceType[] = [
    "user",
    "fund",
    "client",
    "report",
    "transaction",
    "instrument",
    "investor",
    "reporting_period",
    "import",
    "system",
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <RiSearch2Line className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by user, resource, or details..."
            value={filters.searchTerm}
            onChange={(e) => updateFilters({ searchTerm: e.target.value })}
            className="pl-10"
          />
        </div>

        {/* Filter Button with Count */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <RiFilterLine className="h-4 w-4" />
              <span>Filters</span>
              {getActiveFilterCount(filters) > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {getActiveFilterCount(filters)}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4">
            <div className="space-y-4">
              <h3 className="font-medium">Filter Audit Logs</h3>

              {/* User Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">User</label>
                <Select
                  onValueChange={(value) =>
                    updateFilters({ users: value ? [value] : [] })
                  }
                  value={filters.users[0] || ""}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Users</SelectItem>
                    {uniqueUsers().map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Type Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Action Type</label>
                <Select
                  onValueChange={(value) =>
                    updateFilters({
                      actions: value ? [value as AuditActionType] : [],
                    })
                  }
                  value={filters.actions[0] || ""}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Actions</SelectItem>
                    {actionTypes.map((action) => (
                      <SelectItem key={action} value={action}>
                        {formatActionType(action)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Resource Type Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Resource Type</label>
                <Select
                  onValueChange={(value) =>
                    updateFilters({
                      resources: value ? [value as AuditResourceType] : [],
                    })
                  }
                  value={filters.resources[0] || ""}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select resource" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Resources</SelectItem>
                    {resourceTypes.map((resource) => (
                      <SelectItem key={resource} value={resource}>
                        {formatResourceType(resource)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Date Range</label>
                <div className="flex flex-col gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      From date
                    </label>
                    <DatePicker
                      date={filters.dateRange.from}
                      onSelect={(date) =>
                        updateFilters({
                          dateRange: { ...filters.dateRange, from: date },
                        })
                      }
                      placeholder="From date"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      To date
                    </label>
                    <DatePicker
                      date={filters.dateRange.to}
                      onSelect={(date) =>
                        updateFilters({
                          dateRange: { ...filters.dateRange, to: date },
                        })
                      }
                      placeholder="To date"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button
                  variant="ghost"
                  onClick={resetFilters}
                  className="flex items-center gap-1"
                >
                  <RiCloseLine className="h-4 w-4" />
                  <span>Reset</span>
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Reset Button (only shown when filters are active) */}
        {getActiveFilterCount(filters) > 0 && (
          <Button
            variant="ghost"
            onClick={resetFilters}
            className="flex items-center gap-1"
          >
            <RiCloseLine className="h-4 w-4" />
            <span>Clear Filters</span>
          </Button>
        )}
      </div>

      {/* Active Filters */}
      {getActiveFilterCount(filters) > 0 && (
        <div className="flex flex-wrap gap-2 pt-2">
          {filters.users.length > 0 && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1"
            >
              <span>User: {getUsername(filters.users[0], uniqueUsers())}</span>
              <RiCloseLine
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilters({ users: [] })}
              />
            </Badge>
          )}
          {filters.actions.length > 0 && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1"
            >
              <span>Action: {formatActionType(filters.actions[0])}</span>
              <RiCloseLine
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilters({ actions: [] })}
              />
            </Badge>
          )}
          {filters.resources.length > 0 && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1"
            >
              <span>Resource: {formatResourceType(filters.resources[0])}</span>
              <RiCloseLine
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilters({ resources: [] })}
              />
            </Badge>
          )}
          {filters.dateRange.from && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1"
            >
              <span>From: {format(filters.dateRange.from, "PP")}</span>
              <RiCloseLine
                className="h-3 w-3 cursor-pointer"
                onClick={() =>
                  updateFilters({
                    dateRange: { ...filters.dateRange, from: null },
                  })
                }
              />
            </Badge>
          )}
          {filters.dateRange.to && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1"
            >
              <span>To: {format(filters.dateRange.to, "PP")}</span>
              <RiCloseLine
                className="h-3 w-3 cursor-pointer"
                onClick={() =>
                  updateFilters({
                    dateRange: { ...filters.dateRange, to: null },
                  })
                }
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

// Helper functions
function getActiveFilterCount(filters: FilterOptions): number {
  let count = 0;
  if (filters.users.length > 0) count++;
  if (filters.actions.length > 0) count++;
  if (filters.resources.length > 0) count++;
  if (filters.dateRange.from) count++;
  if (filters.dateRange.to) count++;
  return count;
}

function formatActionType(action: AuditActionType): string {
  return action.charAt(0).toUpperCase() + action.slice(1);
}

function formatResourceType(resource: AuditResourceType): string {
  return resource
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getUsername(
  userId: string,
  users: { id: string; name: string; email: string }[]
): string {
  const user = users.find((u) => u.id === userId);
  return user ? user.name : userId;
}
